import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Tab, Nav } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { createPerson } from '../../services/Person/personService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import InputField from '../Utils/InputField';
import UFSelect from '../Utils/UFSelect';
import DocumentInput from '../Utils/DocumentInput';
import PhoneInput from '../Utils/PhoneInput';
import ZipCodeInput from '../Utils/ZipCodeInput';
import LicensePlateInput from '../Utils/LicensePlateInput';
import DecimalInput from '../Utils/DecimalInput';

const CreatePersonModal = ({ show, handleClose, refreshPersons, onCreated }) => {
  const { register, handleSubmit, formState: { errors }, reset, watch, control } = useForm({
    defaultValues: {
      active: true,
      isCustomer: true,
      isSupplier: true,
      isDriver: true,
      isProducer: true,
      discount: 0,
    }
  });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeKey, setActiveKey] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isCustomer = watch('isCustomer');
  const isSupplier = watch('isSupplier');
  const isDriver = watch('isDriver');
  const isProducer = watch('isProducer');

  const validateCheckboxes = () => {
    return isCustomer || isSupplier || isDriver || isProducer || 'Pelo menos um tipo deve ser selecionado.';
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const created = await createPerson(data);
      
      if (onCreated) {
        onCreated({
          id: created.uuid,
          name: created.name,
          discount: created.discount,
        });
      } else {
        setSuccessMessage('Pessoa criada com sucesso!');
        setShowSuccessModal(true);
      }

      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao criar pessoa.');
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshPersons();
    handleClose();
  };

  useEffect(() => {
    if (show) {
      reset();
    }
  }, [show, reset]);

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cadastrar Pessoa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
          >
            <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="general">Informações Gerais</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="address">Endereço</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="general">
                  <Row>
                    <Col md={8}>
                      <InputField
                        label="Nome"
                        name="name"
                        placeholder="Digite o nome"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Nome é obrigatório' }}
                      />
                    </Col>
                    <Col md={4}>
                      <DocumentInput
                        label="Documento"
                        name="document"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Documento é obrigatório' }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3}>
                      <InputField
                        label="Matrícula"
                        name="registration"
                        placeholder="Digite a matrícula"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={3}>
                      <PhoneInput
                        label="Telefone Comercial"
                        name="businessPhone"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={3}>
                      <PhoneInput
                        label="Telefone Celular"
                        name="cellPhone"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={3}>
                      <Controller
                        name="discount"
                        control={control}
                        render={({ field, fieldState }) => (
                          <DecimalInput
                            value={field.value || 0}
                            onChange={field.onChange}
                            label="Taxa de Serviço %"
                            placeholder=" "
                            isInvalid={!!fieldState.error}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <InputField
                        label="Email"
                        name="mail"
                        placeholder="Digite o email"
                        register={register}
                        errors={errors}
                        rules={{
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: 'Endereço de email inválido',
                          }
                        }}
                      />
                    </Col>
                    <Col md={3}>
                      <LicensePlateInput
                        label="Placa do Veículo"
                        name="licensePlate"
                        register={register}
                        errors={errors}
                        placeholder="Placa"
                        value={watch('licensePlate') || ''}
                      />
                    </Col>
                    <Col md={5}>
                      <InputField
                        label="Observações"
                        name="observations"
                        placeholder="Digite observações"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>
                <Tab.Pane eventKey="address">
                  <Row>
                    <Col md={6}>
                      <InputField
                        label="Endereço"
                        name="address"
                        placeholder="Digite o endereço"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={6}>
                      <InputField
                        label="Bairro"
                        name="district"
                        placeholder="Digite o bairro"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={5}>
                      <InputField
                        label="Cidade"
                        name="city"
                        placeholder="Digite a cidade"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={4}>
                      <UFSelect
                        label="Estado"
                        name="state"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={3}>
                    <ZipCodeInput
                      label="CEP"
                      name="zipCode"
                      register={register}
                      errors={errors}
                    />
                    </Col>
                  </Row>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit" disabled={isSubmitting}>
              Cadastrar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default CreatePersonModal;